import { useCallback, useEffect, useState } from 'react';
import { getOrderMetrics } from './dashboardApi';
import type { OrderMetricsPeriod } from './dashboardApi';
import { getDateRangeForBaseDate, getTodayDateStringKST } from '@/shared/lib/dashboard/dateRange';

export type DashboardWeeklyDateParam = string | undefined;

export interface DailyAggregatedMetrics {
  date: string;
  dateLabel: string;
  received: number;
  processing: number;
  lost: number;
  churnRate: number;
  requote: number;
  unqualified: number;
}

function aggregateByDate(periods: OrderMetricsPeriod[]): DailyAggregatedMetrics[] {
  const byDate = new Map<
    string,
    { received: number; processing: number; lost: number; requote: number; unqualified: number }
  >();

  for (const p of periods) {
    const cur = byDate.get(p.date) ?? {
      received: 0,
      processing: 0,
      lost: 0,
      requote: 0,
      unqualified: 0,
    };
    cur.received += p.receivedCount;
    cur.processing += p.pendingCount;
    cur.lost += p.closedLostCount;
    cur.requote += p.requoteCount;
    cur.unqualified += p.unqualifiedCount;
    byDate.set(p.date, cur);
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, agg]) => {
      const churnRate = agg.received > 0 ? Math.round((agg.lost / agg.received) * 1000) / 10 : 0;
      const [y, m, d] = date.split('-').map(Number);
      const dateLabel = new Date(y, m - 1, d).toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric',
        timeZone: 'Asia/Seoul',
      });
      return {
        date,
        dateLabel,
        received: agg.received,
        processing: agg.processing,
        lost: agg.lost,
        churnRate,
        requote: agg.requote,
        unqualified: agg.unqualified,
      };
    });
}

export interface DashboardWeeklyMetrics {
  daily: DailyAggregatedMetrics[];
  periods: OrderMetricsPeriod[];
}

async function fetchWeeklyMetrics(baseDate: string): Promise<DashboardWeeklyMetrics> {
  const { startDate, endDate } = getDateRangeForBaseDate(baseDate);
  const { periods } = await getOrderMetrics({ startDate, endDate });
  const daily = aggregateByDate(periods);
  return { daily, periods };
}

export function useDashboardWeeklyMetrics(baseDateParam?: DashboardWeeklyDateParam) {
  const baseDate = baseDateParam ?? getTodayDateStringKST();
  const [data, setData] = useState<DashboardWeeklyMetrics | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const mutate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchWeeklyMetrics(baseDate);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [baseDate]);

  useEffect(() => {
    mutate();
  }, [mutate]);

  return {
    data,
    error,
    isLoading,
    mutate,
  };
}
